import { useEffect, useState, type FormEvent } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import type { ForgotPasswordOutcome } from '@/services/auth/authService';
import { validateEmail } from '@/utils/formValidators';
import { showToast } from '@/utils/globalToast';
import { AuthLogo } from './components/AuthLogo';
import styles from './ForgotPasswordForm.module.css';

/**
 * ForgotPasswordForm — inline forgot password flow.
 * Mirrors Flutter WebForgotPasswordEmailScreen + WebLoginBloc._ForgotPasswordEmailTriggeredEvent.
 *
 * Uses the backend endpoint (NOT Firebase sendPasswordResetEmail):
 *   POST /v1/p/account/forget-password { email }
 *
 *   - sent: toast AuthConstants.sentPasswordResetLinkMsg and the form closes; useLogin then
 *     navigates like WebLoginPageConsumer on isSuccess (ProfileInput destination, else /home).
 *     Flutter's inline sentPasswordResetLink text is reset in the same listener, so it never
 *     shows and isn't ported.
 *   - 429: toast ForgotPasswordEmailConstants.forgotPasswordMaxLimit (2 s) and a 1-minute
 *     countdown that keeps Continue disabled
 *   - anything else: toast ErrorConstants.invalidCredentials
 */

interface ForgotPasswordFormProps {
  onBack: () => void;
  onSubmit: (email: string) => Promise<ForgotPasswordOutcome>;
  initialEmail?: string;
}

const RATE_LIMIT_SECONDS = 60;

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `0${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function ForgotPasswordForm({
  onBack,
  onSubmit,
  initialEmail = '',
}: ForgotPasswordFormProps) {
  const { t } = useLocalization();

  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);

  useEffect(() => {
    if (timerSeconds <= 0) return;
    const id = setTimeout(() => setTimerSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [timerSeconds]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isLoading || timerSeconds > 0) return;

    const trimmed = email.trim();
    const validationError = validateEmail(trimmed, t);
    setError(validationError);
    if (validationError) return;

    setIsLoading(true);
    try {
      const outcome = await onSubmit(trimmed.toLowerCase());
      if (outcome === 'sent') {
        showToast('We have sent reset password link to your registered email id.\nClick on the link to reset a password', 'success');
        onBack();
      } else if (outcome === 'rateLimited') {
        setTimerSeconds(RATE_LIMIT_SECONDS);
        showToast(
          'Your request for forgot password max limit is crossed, please wait and request again.',
          'error',
          2000,
        );
      } else {
        showToast('Invalid login credentials', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={onBack}
          aria-label={t('Back')}
        >
          ‹
        </button>

        <AuthLogo />

        <h1 className={styles.title}>{t('Forgot Password ?')}</h1>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="forgot-password-email">
              {t('Registered Email ID')}
            </label>
            <input
              id="forgot-password-email"
              type="email"
              autoFocus
              autoComplete="username"
              placeholder={t('Enter your registered Email ID')}
              className={`${styles.input} ${error ? styles.inputError : ''}`}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              disabled={isLoading}
            />
            {error && <span className={styles.fieldError}>{error}</span>}
          </div>

          {timerSeconds > 0 && (
            <div className={styles.timer} aria-live="polite">{formatCountdown(timerSeconds)}</div>
          )}

          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={isLoading || timerSeconds > 0}
          >
            {t('Continue')}
          </button>
        </form>
      </div>
    </div>
  );
}
